import { formatString } from "@/helpers/formatting";
import { axiosClient } from "@/services/client";
import endpoints from "@/services/endpoints";

import type {
  IPaginatedResponse,
  IResponseMeta,
  ParamRecord,
} from "@/models/common";
import type { CustomAttribute } from "@/models/customAttributes/customAttribute";
import type { IDataService } from "@/models/orchestration";
import type { IUser } from "@/models/users";

export type AttributePayload = Partial<
  Omit<CustomAttribute, "client" | "created_by"> & {
    client?: string;
    created_by?: IUser["id"];
  }
>;

class AttributeBuilderService {
  async getServices(params: ParamRecord): Promise<IDataService[]> {
    const response = await axiosClient.get<{ data: IDataService[] }>(
      endpoints.ATTRIBUTES.SERVICES,
      {
        params,
      }
    );
    return response.data.data;
  }
  async getAttributes(
    params: ParamRecord
  ): Promise<{ data: CustomAttribute[]; meta: IResponseMeta }> {
    const response = await axiosClient.get<IPaginatedResponse<CustomAttribute>>(
      formatString(endpoints.ATTRIBUTES.BASE),
      {
        params,
      }
    );
    return response.data;
  }

  async getAttribute(id: CustomAttribute["id"]): Promise<CustomAttribute> {
    const response = await axiosClient.get<{ data: CustomAttribute }>(
      formatString(endpoints.ATTRIBUTES.SINGLE, id)
    );
    return response.data.data;
  }

  async deleteAttribute(id: CustomAttribute["id"]): Promise<CustomAttribute> {
    const response = await axiosClient.delete<{ data: CustomAttribute }>(
      formatString(endpoints.ATTRIBUTES.SINGLE, id)
    );
    return response.data.data;
  }

  async createAttribute(attribute: Partial<CustomAttribute>) {
    const response = await axiosClient.post<{ data: CustomAttribute }>(
      formatString(endpoints.ATTRIBUTES.BASE),
      attribute
    );
    return response.data.data;
  }

  async updateAttribute(attribute: Partial<CustomAttribute>) {
    if (!attribute.id) {
      return;
    }
    const response = await axiosClient.patch<{ data: CustomAttribute }>(
      formatString(endpoints.ATTRIBUTES.SINGLE, attribute.id),
      attribute
    );
    return response.data.data;
  }

  async updateAttributes(attributes: AttributePayload[]) {
    const response = await axiosClient.post<{ data: CustomAttribute[] }>(
      endpoints.ATTRIBUTES.MANY,
      { attributes }
    );
    return response.data.data;
  }
}

const attributeBuilderService = new AttributeBuilderService();
export default attributeBuilderService;
